import { useState } from "react";
import { Briefcase, Globe } from "lucide-react";
import { useData } from "@/context/DataContext";
import { PageHeader, Card } from "@/components/common/PageHeader";
import { StatusChip } from "@/components/common/StatusChip";
import { MatchChip } from "@/components/common/MatchChip";
import { ResearchModal } from "@/components/ai/ResearchModal";
import { Loading } from "@/pages/_shared";

const STATUS_ORDER = ["ALL", "MATCHED", "ELIGIBLE", "INVITED", "PREQUALIFIED", "PREAPPROVED", "APPROVED"];

export default function CapitalMarketplace() {
  const { snapshot } = useData();
  const [filter, setFilter] = useState("ALL");
  const [research, setResearch] = useState(null);
  if (!snapshot) return <Loading />;
  const offers = snapshot.capital_marketplace.offers;
  const shown = filter === "ALL" ? offers : offers.filter((o) => o.status === filter);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="capital-marketplace-page">
      <PageHeader eyebrow="Capital Marketplace" title="Capital Marketplace"
        subtitle="Real marketplace architecture. Statuses are precise and never interchangeable — MATCHED, ELIGIBLE, INVITED, PREQUALIFIED, PREAPPROVED and APPROVED each mean something specific." />

      <div className="flex flex-wrap gap-2">
        {STATUS_ORDER.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`marketplace-filter-${s.toLowerCase()}`}
            className={`rounded-lg px-3 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-wider transition-colors ${
              filter === s ? "bg-[#080d1a] text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-slate-300"
            }`}>
            {s}
          </button>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {shown.map((o, i) => (
          <Card key={i} className="flex flex-col p-5" data-testid="marketplace-offer-card">
            <div className="flex items-start justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50"><Briefcase size={18} className="text-[#1a5fd0]" /></div>
              <MatchChip status={o.status} />
            </div>
            <div className="mt-3 font-head text-[15px] font-bold text-slate-900">{o.name}</div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{o.category}</div>
            <div className="mt-3 font-head text-2xl font-extrabold text-slate-900">{o.amount}</div>
            <div className="text-[12px] text-slate-500">{o.terms}</div>
            <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
              <span className="text-[12px] text-slate-500">{o.provider}</span>
              <StatusChip state={o.state} />
            </div>
            <button className="mt-3 rounded-lg bg-[#1a5fd0] py-2 text-[13px] font-semibold text-white transition-colors hover:bg-[#154ec2]" data-testid="marketplace-offer-action">
              {o.status === "PREAPPROVED" || o.status === "PREQUALIFIED" ? "Review offer" : o.status === "INVITED" ? "Accept invite" : "View details"}
            </button>
            <button onClick={() => setResearch(o)} data-testid="marketplace-research-btn"
              className="mt-2 flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 py-2 text-[12px] font-semibold text-slate-700 transition-colors hover:border-[#1a5fd0]/40 hover:text-[#1a5fd0]">
              <Globe size={13} /> Research this
            </button>
          </Card>
        ))}
      </div>
      {shown.length === 0 && <div className="py-10 text-center text-sm text-slate-400">No offers with status {filter}.</div>}

      <ResearchModal
        open={!!research}
        onClose={() => setResearch(null)}
        title={research ? `Research: ${research.name}` : ""}
        query={research ? `Give current market context for "${research.name}" (${research.category}${research.provider ? `, offered via ${research.provider}` : ""}): typical rates and terms, who it is best for, and what to watch out for. General research only — not an offer or a guarantee of approval.` : ""}
      />
    </div>
  );
}
